import { useState } from "react";
import { apiBase } from "../lib/auth";

/**
 * Magic-link login. Posts an email to the Worker's
 * /v1/insights/manufacturer/auth/login endpoint. v0 just shows the API
 * response shape (501 skeleton) — real magic-link issuance lands when the
 * handler is wired.
 */
export function LoginPage() {
	const [email, setEmail] = useState("");
	const [status, setStatus] = useState<
		| { kind: "idle" }
		| { kind: "submitting" }
		| { kind: "sent" }
		| { kind: "error"; message: string }
		| { kind: "skeleton"; body: unknown }
	>({ kind: "idle" });

	async function submit(e: React.FormEvent) {
		e.preventDefault();
		setStatus({ kind: "submitting" });
		try {
			const res = await fetch(`${apiBase()}/v1/insights/manufacturer/auth/login`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email }),
				credentials: "include",
			});
			if (res.status === 501) {
				const body = await res.json();
				setStatus({ kind: "skeleton", body });
				return;
			}
			if (res.ok) {
				setStatus({ kind: "sent" });
				return;
			}
			const text = await res.text();
			setStatus({ kind: "error", message: `${res.status}: ${text}` });
		} catch (err) {
			setStatus({
				kind: "error",
				message: err instanceof Error ? err.message : String(err),
			});
		}
	}

	return (
		<div style={styles.shell}>
			<div style={styles.card}>
				<div style={styles.brand}>INSIGHTS</div>
				<h1 style={styles.heading}>Manufacturer access</h1>
				<p style={styles.body}>
					Enter your work email. We'll send you a single-use link valid for 15 minutes.
				</p>
				<form onSubmit={submit} style={styles.form}>
					<input
						type="email"
						required
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						placeholder="you@manufacturer.example"
						style={styles.input}
					/>
					<button
						type="submit"
						style={styles.button}
						disabled={status.kind === "submitting"}
					>
						{status.kind === "submitting" ? "Sending…" : "Send magic link"}
					</button>
				</form>

				{status.kind === "sent" && (
					<div style={styles.success}>
						Check your inbox. The link expires in 15 minutes.
					</div>
				)}

				{status.kind === "skeleton" && (
					<div style={styles.skeleton}>
						<div style={{ fontWeight: 600, marginBottom: 6 }}>v0 skeleton response</div>
						<pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 12 }}>
							{JSON.stringify(status.body, null, 2)}
						</pre>
					</div>
				)}

				{status.kind === "error" && (
					<div style={styles.error}>
						<div style={{ fontWeight: 600 }}>Couldn't send the link</div>
						<div style={{ fontSize: 12, marginTop: 4 }}>{status.message}</div>
					</div>
				)}

				<div style={styles.footer}>
					By signing in you accept the{" "}
					<a href="https://aivision.space/trust" target="_blank" rel="noopener">
						Trust Charter
					</a>{" "}
					and the{" "}
					<a href="https://aivision.space/privacy" target="_blank" rel="noopener">
						Privacy Policy
					</a>
					.
				</div>
			</div>
		</div>
	);
}

const styles: Record<string, React.CSSProperties> = {
	shell: {
		minHeight: "100vh",
		display: "grid",
		placeItems: "center",
		padding: 24,
	},
	card: {
		width: "100%",
		maxWidth: 420,
		background: "var(--card-bg)",
		border: "1px solid var(--border)",
		borderRadius: 12,
		padding: 32,
		display: "flex",
		flexDirection: "column",
		gap: 16,
	},
	brand: { fontWeight: 600, fontSize: 14, letterSpacing: -0.2, color: "var(--muted)" },
	heading: { fontSize: 22, fontWeight: 600, margin: 0 },
	body: { fontSize: 14, color: "var(--muted)", margin: 0 },
	form: { display: "flex", flexDirection: "column", gap: 8 },
	input: {
		padding: "10px 12px",
		fontSize: 14,
		border: "1px solid var(--border)",
		borderRadius: 6,
		background: "var(--bg)",
		color: "var(--fg)",
	},
	button: {
		padding: "10px 14px",
		fontSize: 14,
		fontWeight: 600,
		background: "var(--accent)",
		color: "white",
		border: "none",
		borderRadius: 6,
		cursor: "pointer",
	},
	skeleton: {
		background: "rgba(43,95,167,0.08)",
		border: "1px solid var(--accent)",
		padding: 12,
		borderRadius: 6,
		fontSize: 12,
	},
	success: {
		background: "rgba(64,160,80,0.08)",
		border: "1px solid #40a050",
		padding: 12,
		borderRadius: 6,
	},
	error: {
		background: "rgba(180,40,40,0.08)",
		border: "1px solid #c04040",
		padding: 12,
		borderRadius: 6,
	},
	footer: { fontSize: 12, color: "var(--muted)" },
};
